import { z } from 'zod';
import { apiFor } from '@/api/client';

/*
 * Runtime contract for the "Morrisons — Disney × Stickers 2026" dashboard
 * payload (program mr03). THIS SCHEMA IS THE PIPELINE'S CONTRACT:
 * client-portal/pipelines/morrisons-disney-2026 emits exactly this shape.
 *
 * Conventions (as for the Système U dashboard):
 *   - every figure is a RAW NUMBER (counts); the UI formats them;
 *   - a figure the source cannot give is `null` and renders "—" — never 0.
 *     The app-only figures (duplicate scans, scans by method, codes shared)
 *     are null when the app sent no events;
 *   - active users are members who opened at least one flowpack (any source);
 *   - only what the page shows: the figures the design dropped (Eagle Eye,
 *     scanner sessions, first wins, burned packs, points, the per-day table)
 *     are no longer published;
 *   - local days in Europe/London; weeks run Monday → Sunday from go-live
 *     (Mon 2 Nov 2026), week 8 is the four days 21–24 Dec.
 */

const n = z.number();
const nn = z.number().nullable();

/** Stickers scanned on one day — the daily chart. */
const daySchema = z.object({
  /** ISO date, YYYY-MM-DD. Both figures are null for a day still to come. */
  date: z.string(),
  /** Cards added to a collection (a new card for that member). */
  newCards: nn,
  /** Scans of a card the member already owned — app events only. */
  duplicateScans: nn,
});

const weekSchema = z.object({
  week: z.number().int().positive(),
  start: z.string(),
  end: z.string(),
  status: z.enum(['done', 'live', 'planned']),
  /** Members who opened the app that week (counted per week). */
  visitors: nn,
  optIns: nn,
  /** Members who opened at least one flowpack that week. */
  activeUsers: nn,
  newCards: nn,
  duplicateScans: nn,
  flowpacksGranted: nn,
  flowpacksOpened: nn,
  lotteryTickets: nn,
  gamePlays: nn,
  /** Referral codes redeemed. */
  referrals: nn,
  /** Social share missions completed. */
  shares: nn,
});

const gameSchema = z.object({
  /** The game's id ('spot-the-difference' …), in order — each opens on its own date, one a week. */
  id: z.string(),
  name: z.string(),
  position: z.number().int().positive(),
  plays: n,
  wins: n,
  /** Lost because the timer ran out. */
  timeUps: n,
  /** Left the game before the end (or never came back to it). */
  quits: n,
  /**
   * Members who played this game, and of them who won it at least once — the
   * game's win rate is winners ÷ players. Null in a payload from before 3 Nov.
   */
  players: z.number().nullable().default(null),
  winners: z.number().nullable().default(null),
});

const flowpackSourcesSchema = z.object({
  /** One a day to every opted-in visitor; it burns if not opened. */
  daily: n,
  milestone12: n,
  share: n,
  referralInviter: n,
  referralInvitee: n,
  firstWin: n,
  /** Opt-in welcome pack — 0 unless the backend grants one. */
  welcome: n,
  other: n,
});

const prizesSchema = z.object({
  lottery: n,
  points500: n,
  points5000: n,
  freeFruit: n,
  freeBakingKit: n,
  /** Draws with no known prize (unconfigured benefit) — logged by the pipeline. */
  other: n,
});

export const morrisonsPayloadSchema = z.object({
  campaign: z.object({
    name: z.string(),
    start: z.string(),
    end: z.string(),
    timezone: z.string(),
    live: z.boolean(),
    /** Campaign week running now (1-based); 0 before go-live. */
    currentWeek: z.number().int().min(0),
    totalWeeks: z.number().int().positive(),
    collectionSize: z.number().int().positive(),
    manualEntriesPerDay: z.number().int().positive(),
    referralCap: z.number().int().positive(),
    /** Stock of each prize for the whole campaign (Winning Engine "total winners"); null = no limit. */
    prizeCaps: z.object({ lottery: nn, points500: nn, points5000: nn, freeFruit: nn, freeBakingKit: nn }),
  }),
  totals: z.object({
    uniqueVisitors: n,
    /** Views of the unsupported-device screen — not tracked by the app yet. */
    unsupportedDeviceViews: nn,
    optIns: n,
    /** Members who opened at least one flowpack. */
    activeUsers: n,
    /** … of whom on more than one day. */
    returningUsers: n,
    /** Σ days with a claim over all active members (÷ activeUsers = average active days). */
    activeDays: n,
    /** Average members opening a flowpack per elapsed day. */
    dailyActiveAvg: nn,
    newCards: n,
    duplicateScans: nn,
    cameraCards: n,
    manualCards: n,
    /** Every sticker added by camera / by manual entry: new cards plus duplicate scans — null without app events. */
    cameraScans: nn,
    manualScans: nn,
    /** Members who used all their manual entries on at least one day. */
    manualLimitUsers: n,
    /** Members with at least one card. */
    collectors: n,
    /** Members holding at least `cards` cards. */
    milestones: z.array(z.object({ cards: z.number().int().positive(), users: n })),
    flowpacksGranted: flowpackSourcesSchema,
    flowpacksOpened: n,
    prizes: prizesSchema,
    gamePlays: n,
    /**
     * Members with at least one game session (any of the four games, finished or
     * not) — the opt-in → play conversion. Null in a payload from before 3 Nov.
     */
    uniquePlayers: z.number().nullable().default(null),
    /** … of whom won at least one game: the overall win rate is gameWinners ÷ uniquePlayers. Null before 3 Nov. */
    gameWinners: z.number().nullable().default(null),
    referral: z.object({
      /**
       * Codes shared: members' codes used at least once (`referral.used_count <> 0`)
       * — also the number of inviters. Null only in a payload from before 3 Nov,
       * when this was the app's share / copy clicks.
       */
      codesShared: nn,
      /** Codes redeemed: Σ `used_count` — every friend who joined with a code. */
      redeemed: n,
      inviters: n,
      invitersAtMax: n,
      inviteeOptIns: n,
    }),
    shares: n,
    sharers: n,
  }),
  days: z.array(daySchema),
  weeks: z.array(weekSchema),
  /** New-card scans per card number, 001–120. */
  cards: z.array(z.object({ card: z.string(), collected: n })),
  games: z.array(gameSchema),
});

export const morrisonsEnvelopeSchema = z.object({
  data: morrisonsPayloadSchema,
  meta: z.object({ last_refreshed_at: z.string().nullable() }).partial().optional(),
});

export type MorrisonsPayload = z.infer<typeof morrisonsPayloadSchema>;
export type MorrisonsDay = z.infer<typeof daySchema>;
export type MorrisonsWeek = z.infer<typeof weekSchema>;
export type MorrisonsGame = z.infer<typeof gameSchema>;
export type MorrisonsEnvelope = z.infer<typeof morrisonsEnvelopeSchema>;

/** Same generic report endpoint every dashboard uses: GET <base>/reports/<id>. */
export async function getMorrisonsReport(reportId: string, baseUrl: string): Promise<MorrisonsEnvelope> {
  const raw = await apiFor(baseUrl).get(`reports/${reportId}`).json();
  return morrisonsEnvelopeSchema.parse(raw);
}
